import { FC, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import { ValidIcons, Icon } from '@kizen/kds/Icon';
import { Button } from '@kizen/kds/Button';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { CardBasic } from '__components/Card/Card';
import { TypingIndicator } from '../Chat/TypingIndicator';
import Loader from '__components/Kizen/Loader';
import { Tooltip } from '@kizen/kds/Tooltip';

interface Entity {
  id?: string;
  name: string;
  display_name: string;
  status: 'ready' | 'building' | 'planning' | 'failed' | 'next_steps';
  description?: string;
  elapsed_time?: string;
  url?: string;
}

interface FlowStatusMetadata {
  custom_object_generation: Record<string, Entity>;
  custom_object_sample_generation: Record<string, Entity>;
  custom_objects: Record<string, Entity>;
  activities: Record<string, Entity>;
  custom_object: Record<string, Entity>;
  homepages: Record<string, Entity>;
  strategic_plan: Record<string, Entity>;
  status: string;
}

interface BusinessGenerationSummaryProps {
  isMobile: boolean;
  isStartGenerationLoading: boolean;
  flowStatusMetadata: FlowStatusMetadata;
  flowStatus: string;
  labels: {
    name: keyof FlowStatusMetadata;
    label: string;
  }[];
  createdAt?: string;
}

interface ExpandableSummaryProps {
  entities: Record<string, Entity>;
  label: string;
  type: keyof FlowStatusMetadata;
}

type GenerationStatusType =
  | 'ready'
  | 'building'
  | 'planning'
  | 'failed'
  | 'next_steps'
  | 'completed';

interface GenerationStatusProps {
  status: GenerationStatusType;
  subStatus?: {
    key: keyof FlowStatusMetadata;
    status: GenerationStatusType;
  } | null;
  isMobile: boolean;
}

const STATUS_COMPLETED = 'completed';
const STATUS_READY = 'ready';
const STATUS_BUILDING = 'building';
const STATUS_PLANNING = 'planning';
const STATUS_FAILED = 'failed';
const STATUS_NEXT_STEPS = 'next_steps';

const subStatusLabelsByStatus = {
  [STATUS_PLANNING]: [
    'Analyzing your strategic plan',
    'Ensuring compliance of your plan and application',
    'Planning custom objects',
    'Optimizing custom object structures',
    'Planning activities',
    'Planning component dependencies',
  ],
  [STATUS_BUILDING]: [
    'Validating custom object generation',
    'Validating activity generation',
    'Validating relationships between objects',
    'Building validated objects in the system',
    'Create samples for custom objects',
  ],
};

const getTextColorByStatus = (status: GenerationStatusType) => {
  switch (status) {
    case STATUS_READY:
      return 'feedback-info';
    case STATUS_BUILDING:
      return 'button-primary-pressed';
    case STATUS_PLANNING:
      return 'background-dark';
    case STATUS_FAILED:
      return 'feedback-error';
    case STATUS_NEXT_STEPS:
      return 'button-primary-pressed';
    default:
      return 'font-placeholder';
  }
};
const getBaseEntityLink = (type: string) => {
  switch (type) {
    case 'custom_objects':
      return '/custom-objects';
    case 'activities':
      return '/activities';
    case 'homepages':
      return '/home';
    case 'form_surveys':
      return '/surveys';
    case 'automations':
      return '/agentic-workflows';
    case 'roles':
      return 'settings/team/roles';
    case 'email_templates':
      return 'library/templates';
    case 'toolbar_templates':
      return '/account/toolbar';
    default:
      return undefined;
  }
};
const pipeToTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const GenerationStatus: FC<GenerationStatusProps> = ({
  status,
  subStatus,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [currentSubStatusLabel, setCurrentSubStatusLabel] = useState<
    string | null
  >('Planning Custom Objects');
  const [fadeIn, setFadeIn] = useState(true);
  const [subStatusLabel, setSubStatusLabel] = useState(currentSubStatusLabel);
  const prevStatusRef = useRef<string>(status);
  const overrideTimeoutRef = useRef<boolean>(false);

  useEffect(() => {
    setFadeIn(false);
    const timer = setTimeout(
      () => {
        setSubStatusLabel(currentSubStatusLabel);
      },
      overrideTimeoutRef.current ? 0 : 700
    );
    return () => clearTimeout(timer);
  }, [currentSubStatusLabel, setSubStatusLabel]);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setFadeIn(true);
        if (overrideTimeoutRef.current) {
          overrideTimeoutRef.current = false;
        }
      },
      overrideTimeoutRef.current ? 0 : 700
    );
    return () => clearTimeout(timer);
  }, [subStatusLabel]);

  // timer for substatuses
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (subStatus && subStatus.status && status) {
      const availableLabels =
        subStatusLabelsByStatus[
          subStatus.status as keyof typeof subStatusLabelsByStatus
        ]?.filter((label) => label !== currentSubStatusLabel) || [];
      if (availableLabels.length > 0) {
        if (prevStatusRef.current !== status) {
          overrideTimeoutRef.current = true;
          prevStatusRef.current = status;
          setCurrentSubStatusLabel(availableLabels[0]);
        }
        intervalId = setInterval(() => {
          const randomIndex = Math.floor(
            Math.random() * availableLabels.length
          );
          setCurrentSubStatusLabel(availableLabels[randomIndex]);
        }, 10000);
      }
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [subStatus, currentSubStatusLabel, status]);

  return (
    <div className="animate-fadeIn">
      {status === STATUS_COMPLETED ? (
        <div className="flex justify-start items-center gap-[5px] animate-fadeIn mt-[15px] flex-nowrap">
          <Icon icon="custom-badge-check" />
          <Typography weight="semibold">{t('Completed')}</Typography>
        </div>
      ) : status === STATUS_FAILED ? (
        <div className="flex justify-start items-center gap-[5px] animate-fadeIn mt-[15px] flex-nowrap">
          <Icon color="feedback-error" icon="message-info-circle" />
          <Typography weight="semibold">{t('Failed')}</Typography>
        </div>
      ) : status ? (
        <div
          className={
            isMobile
              ? ' flex flex-col justify-start items-start pb-[5px] gap-[10px] animate-fadeIn mt-[5px] relative'
              : 'flex flex-col justify-start items-start pb-[5px] gap-[10px] animate-fadeIn mt-[10px] relative'
          }
        >
          <div className="flex justify-start items-center gap-[5px]">
            <div className="flex justify-center items-end mt-[4px]">
              <Loader size={10} loading />
            </div>
            <Typography weight="bold" className="pt-[3px] animate-fadeIn">
              {pipeToTitleCase(status)}
            </Typography>
          </div>
          <div
            key={currentSubStatusLabel}
            style={{
              position: 'absolute',
              opacity: fadeIn ? 1 : 0,
              transition: 'opacity 1s',
              top: isMobile ? '30px' : '35px',
              whiteSpace: 'nowrap',
            }}
          >
            <TypingIndicator
              label={subStatusLabel}
              size="sm"
              weight="semibold"
              color="font-secondary"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
const ExpandableSummary: FC<ExpandableSummaryProps> = ({
  entities = {},
  label,
  type,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const initialOpenRef = useRef(true);
  const { t } = useTranslation();
  const tooltipTargetRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const toggleExpansion = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const entityStatus: { label: string; status: string } = useMemo(() => {
    if (
      Object.values(entities).every(
        (entity: Entity) =>
          entity.status === STATUS_READY || entity.status === STATUS_NEXT_STEPS
      )
    ) {
      return { status: STATUS_READY, label: t('Ready') };
    } else if (
      Object.values(entities).some(
        (entity: Entity) => entity.status === STATUS_BUILDING
      )
    ) {
      return { status: STATUS_BUILDING, label: t('Building...') };
    } else if (
      Object.values(entities).some(
        (entity: Entity) => entity.status === STATUS_FAILED
      )
    ) {
      return {
        status: STATUS_FAILED,
        label: t('Completed with warnings'),
      };
    } else {
      return { status: STATUS_PLANNING, label: t('Planning...') };
    }
  }, [entities, t]);

  useEffect(() => {
    if (Object.keys(entities).length > 0 && initialOpenRef.current) {
      initialOpenRef.current = false;
      toggleExpansion();
    }
  }, [entities, toggleExpansion, entityStatus]);

  const entityProgressStatus: string = useMemo(() => {
    const numberOfReady = Object.values(entities).reduce((acc, entity) => {
      if (entity.status === STATUS_READY) {
        return acc + 1;
      }
      return acc;
    }, 0);
    return `(${numberOfReady} / ${Object.keys(entities).length})`;
  }, [entities]);

  if (Object.values(entities).length === 0) {
    return null;
  }

  return (
    <CardBasic className="pl-[15px] pb-[10px] pt-[5px] pr-[10px] animate-fadeIn">
      <div className="pb-[5px] flex justify-between mr-[10px] items-center">
        <div className="flex justify-center items-center gap-[10px]">
          <Typography weight="bold">{label}</Typography>
          <Typography
            color={getTextColorByStatus(
              entityStatus.status as GenerationStatusType
            )}
            variant={entityStatus.status === STATUS_READY ? 'link' : 'body'}
            target={entityStatus.status === STATUS_READY ? '_blank' : undefined}
            href={
              entityStatus.status === STATUS_READY
                ? getBaseEntityLink(type)
                : undefined
            }
          >
            {entityStatus.label}
          </Typography>
        </div>
        <div className="flex justify-start gap-[5px] items-center">
          <Typography weight="bold">{entityProgressStatus}</Typography>
          <Button
            rightIcon={
              (isExpanded
                ? 'form-input-dropdown-arrow-up'
                : 'form-input-dropdown-arrow') as ValidIcons
            }
            rightIconSettings={{
              tooltipTitleOverride: isExpanded ? 'Collapse' : 'Expand',
              color: 'font-primary',
            }}
            hoverColor="rgba(var(--color-button-tertiary-hover)"
            variant="text"
            color="inherit"
            onClick={toggleExpansion}
          />
        </div>
      </div>
      <div
        className={`ml-[5px] pl-[10px] flex flex-col gap-[20px] animate-fadeIn overflow-hidden ${isExpanded ? 'h-[calc-size(auto)]' : 'h-0'}`}
      >
        {Object.keys(entities)?.map((key: string) => {
          const status = entities[key].status as GenerationStatusType;
          const pendingStatus = [STATUS_BUILDING, STATUS_PLANNING];
          const subEntityStatus = `${pipeToTitleCase(entities[key].status)} ${pendingStatus.includes(status) ? '...' : ''}`;
          const failedMessage = t('Failed due to missing requirement');
          const nextStepsMessage = t('Object generation is not yet available');

          return (
            <div
              className="flex flex-col gap-[15px] animate-fadeIn pb-[5px]"
              key={entities[key].display_name}
            >
              <div className="flex justify-start gap-[10px] items-center animate-fadeIn">
                <Typography weight="semibold">
                  {entities[key].display_name ?? entities[key].name}
                </Typography>
                <span
                  key={entities[key].display_name}
                  className="flex gap-[5px] items-center"
                >
                  <Typography
                    size="sm"
                    color={getTextColorByStatus(entities[key].status)}
                    variant={entities[key].url ? 'link' : 'body'}
                    target={entities[key].url ? '_blank' : undefined}
                    href={entities[key].url}
                  >
                    {subEntityStatus}
                  </Typography>

                  {status === STATUS_FAILED || status === STATUS_NEXT_STEPS ? (
                    <span
                      key={entities[key].display_name}
                      onMouseEnter={() => setHoveredKey(key)}
                      onMouseLeave={() => setHoveredKey(null)}
                      ref={(el) => (tooltipTargetRefs.current[key] = el)}
                    >
                      <Icon
                        className={
                          status === STATUS_FAILED
                            ? 'hover:text-feedback-toast-error'
                            : 'hover:text-button-primary-pressed'
                        }
                        icon="message-info-circle"
                        color={
                          STATUS_FAILED === status
                            ? 'feedback-error'
                            : 'button-primary-pressed'
                        }
                        size="sm"
                      />
                      {hoveredKey === key &&
                      (status === STATUS_FAILED ||
                        status === STATUS_NEXT_STEPS) ? (
                        <Tooltip
                          position="top"
                          text={
                            status === STATUS_FAILED
                              ? failedMessage
                              : nextStepsMessage
                          }
                          modalCompatibility
                          target={tooltipTargetRefs.current[key]}
                        />
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </div>
              {entities[key].description ? (
                <div className="flex animate-fadeIn">
                  <Typography size="sm">{entities[key].description}</Typography>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </CardBasic>
  );
};
export const BusinessGenerationSummary: FC<BusinessGenerationSummaryProps> = ({
  isMobile,
  isStartGenerationLoading,
  flowStatusMetadata,
  flowStatus,
  labels = [],
  createdAt = '',
}) => {
  const { t } = useTranslation();
  const [isScrolledTop, setIsScrolledTop] = useState(true);
  const [isScrolledBottom, setIsScrolledBottom] = useState(true);
  const summaryScrollRef = useRef<HTMLDivElement>(null);
  const [showCTA, setShowCTA] = useState(false);

  useEffect(() => {
    if (!createdAt) return;

    const check = () => {
      const now = new Date();
      const createdAtDate = new Date(createdAt);
      const timeDiff = now.getTime() - createdAtDate.getTime();
      const timeDiffInMinutes = Math.floor(timeDiff / 1000 / 60);
      setShowCTA(timeDiffInMinutes >= 3);
    };

    check();
    const interval = setInterval(check, 1000);

    return () => clearInterval(interval);
  }, [createdAt]);

  // calculate if scrolled to top
  useEffect(() => {
    const currentRef = summaryScrollRef.current;
    const handleScroll = () => {
      setIsScrolledTop(currentRef?.scrollTop === 0);
    };
    currentRef?.addEventListener('scroll', handleScroll);

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // calculate if scrolled to bottom
  useEffect(() => {
    const currentRef = summaryScrollRef.current;
    const handleScroll = () => {
      if (currentRef) {
        const isScrolledBottom =
          currentRef?.scrollHeight - currentRef?.scrollTop <=
          currentRef?.clientHeight + 1; // Add a small threshold
        setIsScrolledBottom(isScrolledBottom);
      }
    };

    currentRef?.addEventListener('scroll', handleScroll);

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll);
    };
  }, [summaryScrollRef]);

  const {
    generationStatus,
    generationSubStatus,
  }: {
    generationStatus?: GenerationStatusType;
    generationSubStatus: {
      key: keyof FlowStatusMetadata;
      status: GenerationStatusType;
    } | null;
  } = useMemo(() => {
    let subStatus = null;

    if (labels) {
      for (const { name } of labels) {
        const entitiesObject = flowStatusMetadata?.[name];

        if (entitiesObject) {
          const items = Object.values(entitiesObject);

          const planningEntity = items.find(
            (item: Entity) => item.status === STATUS_PLANNING
          );
          const buildingEntity = items.find(
            (item: Entity) => item.status === STATUS_BUILDING
          );

          if (planningEntity) {
            subStatus = { key: name, status: STATUS_PLANNING } as {
              key: keyof FlowStatusMetadata;
              status: GenerationStatusType;
            };
            break;
          } else if (buildingEntity) {
            subStatus = { key: name, status: STATUS_BUILDING } as {
              key: keyof FlowStatusMetadata;
              status: GenerationStatusType;
            };
            break;
          }
        }
      }
    }
    return {
      generationStatus: flowStatus as GenerationStatusType,
      generationSubStatus: subStatus || null,
    };
  }, [flowStatusMetadata, flowStatus, labels]);

  if (
    isStartGenerationLoading ||
    Object.keys(flowStatusMetadata ?? {}).length === 0
  ) {
    return null;
  }

  return (
    <div className="h-full w-full grid overflow-auto place-items-center animate-fadeIn">
      <div className="pl-[20px] pr-[20px] w-full">
        <div className="flex justify-center animate-fadeIn"></div>
        <div
          className={
            isMobile
              ? 'flex flex-col items-center justify-center gap-[5px]'
              : 'flex justify-center items-center gap-[40px]'
          }
        >
          <div className={isMobile ? 'w-full order-2' : 'w-full'}>
            <div className="flex flex-col justify-between h-full">
              <div className="relative flex justify-start flex-col">
                <div
                  className={
                    isMobile
                      ? 'border-1 border-border-medium rounded-[8px] bg-background-light order-2 mb-[10px]'
                      : 'border-1 border-border-medium rounded-[8px] bg-background-light mr-[1px] py-[2px]'
                  }
                >
                  <div
                    ref={summaryScrollRef}
                    className={
                      isMobile
                        ? 'flex flex-col p-[20px] pb-[10px] gap-[10px]'
                        : 'flex flex-col overflow-y-scroll kds-expanding-scrollbar h-[700px] p-[20px] pb-[10px] gap-[10px]'
                    }
                  >
                    <div
                      className={
                        isMobile
                          ? 'absolute top-[41px] left-0 w-[calc(100%-2px)] h-[30px]'
                          : 'absolute top-[2px] left-0 w-[calc(100%-2px)] h-[30px]'
                      }
                    >
                      <Gradient
                        style={{ borderRadius: '16px' }}
                        fadeStart={0}
                        fadeLength={50}
                        color="var(--color-background-light)"
                        position="top"
                        fadeIn={!isScrolledTop && !isMobile}
                        zIndex={1}
                        borderRadius="8px 8px 8px 8px"
                      />
                    </div>
                    {labels?.map(({ name, label }) => (
                      <div key={name}>
                        <ExpandableSummary
                          entities={
                            flowStatusMetadata?.[name] as Record<string, Entity>
                          }
                          label={label}
                          type={name}
                        />
                      </div>
                    ))}
                    <div className="absolute bottom-[31px] left-0 w-[calc(100%-2px)] h-[30px]">
                      <Gradient
                        fadeStart={0}
                        fadeLength={50}
                        color="var(--color-background-white)"
                        position="bottom"
                        fadeIn={!isScrolledBottom && !isMobile}
                        zIndex={1}
                        borderRadius="8px 8px 8px 8px"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={
                    generationStatus === STATUS_READY && isMobile
                      ? 'w-full h-[20px] flex flex-col-reverse pb-[50px] pt-[56.25%] justify-start items-start mb-[10px] px-[5px] gap-[10px]'
                      : isMobile
                        ? 'w-full h-[20px] flex flex-col-reverse pb-[50px] pt-[35%] justify-between items-start pl-[5px] gap-[20px]'
                        : 'w-full h-[20px] flex pb-[50px] justify-between items-start mt-[10px] pl-[5px]'
                  }
                >
                  <GenerationStatus
                    isMobile={isMobile}
                    status={generationStatus as GenerationStatusType}
                    subStatus={generationSubStatus}
                  />
                  <div
                    className={
                      isMobile
                        ? 'flex gap-[15px] w-full justify-center items-center'
                        : 'flex gap-[15px] pb-[50px] justify-center items-center pt-[5px]'
                    }
                  >
                    <a
                      href="/settings"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="text" color="secondary">
                        {t('Refine')}
                      </Button>
                    </a>
                    <a
                      href="/smartconnectors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button color="secondary" outline onClick={() => {}}>
                        {t('Upload Data')}
                      </Button>
                    </a>
                    <a
                      href="/settings/team"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button color="secondary" outline>
                        {t('Invite Team')}
                      </Button>
                    </a>
                    {showCTA && isMobile ? (
                      <a href="/home">
                        <Button maxWidth={false}>
                          {t('Explore Your Business')}
                        </Button>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full h-full flex flex-col justify-center items-center mt-[10px] animate-fadeIn">
            <div className="relative w-full animate-fadeIn aspect-[4/3]">
              <video
                loop
                muted
                autoPlay
                playsInline
                className="absolute top-0 left-0 w-full h-full border-0 object-cover"
              >
                <source
                  src="https://kizen.com/media/kizen-builder-intro.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
            {showCTA && !isMobile ? (
              <div className="w-full flex justify-center items-center pt-[20px] animate-fadeIn">
                <a href="/home">
                  <Button maxWidth={false}>{t('Explore Your Business')}</Button>
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
